import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { 
  getCartsByDocument,
  getCartsByEmail,
  getCartDetails
} from "../../services/user";

import ButtonBack from '../../components/ButtonBack';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import Text from '../../components/Text';
import Input from '../../components/Form/Input';
import Spacing from '../../components/Spacing';
import { FormGroup } from '../../components/Form';
import ErrorBox from '../../components/ErrorBox';

import { 
  Container,
  TabHeader,
  CartItemContainer,
  CartItemHeader,
  CartItemsList,
  ItemRow,
  CartItemDetails,
  CartDetailsSection,
  CartEventTimeline,
  CartEvent,
  CartEventTime,
  CartEventType,
  CartEventDetail,
  SearchBar,
  NoResultsMessage,
  PromotionTag,
  CartSummary,
  ActionButtonsContainer
} from './styles';

// Esquema de validação do formulário de busca
const searchSchema = Yup.object().shape({
  searchTerm: Yup.string()
    .required('Digite um CPF/CNPJ ou email para pesquisar')
});

// Main component
const CartTracking = () => {
  const history = useHistory();
  const [searchType, setSearchType] = useState('document'); // 'document' or 'email'
  const [carts, setCarts] = useState([]);
  const [selectedCart, setSelectedCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // Handle search submission
  const handleSearch = async (values) => {
    if (!values.searchTerm) {
      Swal.fire({
        icon: "error",
        title: "Atenção",
        text: "Digite um CPF/CNPJ ou email para pesquisar",
        confirmButtonColor: "#CB3D42",
      });
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (searchType === 'document') {
        response = await getCartsByDocument(values.searchTerm);
      } else {
        response = await getCartsByEmail(values.searchTerm);
      }
      
      setCarts(response.data || []);
      setSelectedCart(null);
      setSearched(true);
    } catch (err) {
      console.error("Erro ao buscar dados do carrinho:", err);
      setError(err.message || "Houve um erro ao buscar os dados do carrinho. Tente novamente.");
      setCarts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch cart details
  const fetchCartDetails = async (cartId) => {
    setLoading(true);
    try {
      const response = await getCartDetails(cartId);
      setSelectedCart(response);
    } catch (err) {
      console.error("Erro ao buscar detalhes do carrinho:", err);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: err.message || "Não foi possível carregar os detalhes do carrinho",
        confirmButtonColor: "#CB3D42",
      });
    } finally {
      setLoading(false);
    }
  };

  // Gerar link para o carrinho
  const generateCartLink = (cart) => {
    try {
      // Base URL da loja
      const baseUrl = 'https://www.zonasul.com.br/cart';
      
      // Obter SKUs e quantidades dos itens
      const skus = [];
      const quantities = [];
      const seller = "zonasulzsa"; // Seller padrão do Zona Sul
      
      // Extrair itens do carrinho
      const items = cart.items || [];
      
      items.forEach(item => {
        const skuId = item.sku_id || item.id;
        skus.push(skuId);
        quantities.push(item.quantity);
      });
      
      // Construir parâmetros da URL
      const skuParams = skus.map(sku => `sku=${sku}`).join('&');
      const qtyParams = quantities.map(qty => `qty=${qty}`).join('&');
      
      // URL completa para o carrinho
      const cartUrl = `${baseUrl}/?${skuParams}&${qtyParams}&seller=${seller}`;
      
      return cartUrl;
    } catch (err) {
      console.error("Erro ao gerar link do carrinho:", err);
      return null;
    }
  };
  
  // Copiar link para o carrinho
  const copyCartLink = (cart) => {
    const cartLink = generateCartLink(cart);
    
    if (!cartLink) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível gerar o link do carrinho",
        confirmButtonColor: "#CB3D42",
      });
      return;
    }
    
    // Copiar para a área de transferência
    navigator.clipboard.writeText(cartLink)
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Link copiado!",
          text: "Link do carrinho copiado para a área de transferência",
          confirmButtonColor: "#CB3D42",
          timer: 2000,
          timerProgressBar: true,
        });
      })
      .catch(err => {
        console.error("Erro ao copiar link:", err);
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Não foi possível copiar o link do carrinho",
          confirmButtonColor: "#CB3D42",
        });
      });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  // Format currency for display
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100); // Convertendo de centavos para reais
  };

  // Get event description based on type
  const getEventDescription = (eventType) => {
    const eventDescriptions = {
      'item_added': 'Item adicionado ao carrinho',
      'item_removed': 'Item removido do carrinho',
      'item_updated': 'Quantidade atualizada',
      'address_added': 'Endereço adicionado',
      'address_updated': 'Endereço atualizado',
      'promotion_applied': 'Promoção aplicada',
      'promotion_removed': 'Promoção removida',
      'payment_started': 'Processo de pagamento iniciado',
      'payment_completed': 'Pagamento concluído',
      'cart_abandoned': 'Carrinho abandonado',
      'cart_shared': 'Carrinho compartilhado'
    };
    
    return eventDescriptions[eventType] || eventType;
  };

  return (
    <>
      <ButtonBack onClick={() => history.goBack()} />
      <Container>
        <TabHeader>
          <Text variant="title1">Rastreamento de Carrinho</Text>
          <Text variant="regular">Busca</Text>
        </TabHeader>

        {error && <ErrorBox error={error} />}

        <Formik
          initialValues={{ searchTerm: '' }}
          validationSchema={searchSchema}
          onSubmit={handleSearch}
        >
          {({ isSubmitting, values, handleChange }) => (
            <Form>
              <SearchBar>
                <FormGroup style={{ flex: 1, marginRight: '10px' }}>
                  <Input
                    type="text"
                    name="searchTerm"
                    placeholder={searchType === 'document' ? "Digite o CPF/CNPJ" : "Digite o email"}
                  />
                </FormGroup>
                
                <FormGroup style={{ width: '150px', marginRight: '10px' }}>
                  <select 
                    style={{ 
                      height: '40px', 
                      width: '100%',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      padding: '0 10px'
                    }}
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                  >
                    <option value="document">CPF/CNPJ</option>
                    <option value="email">Email</option>
                  </select>
                </FormGroup>
                
                <Button
                  variant="buttonPrimary"
                  text="Buscar"
                  type="submit"
                  disabled={loading || isSubmitting}
                  loading={loading || isSubmitting}
                />
              </SearchBar>
            </Form>
          )}
        </Formik>

        {loading && !selectedCart && <Loading text="Buscando..." />}

        {!loading && searched && carts.length === 0 && (
          <NoResultsMessage>
            Nenhum carrinho encontrado para esta pesquisa.
          </NoResultsMessage>
        )}

        {!loading && !selectedCart && carts.length > 0 && (
          <>
            <Text variant="subtitle">Carrinhos encontrados: {carts.length}</Text>
            <Spacing size={15} />
            
            <CartItemsList>
              {carts.map((cart) => (
                <CartItemContainer key={cart.id || cart.orderform_id}>
                  <CartItemHeader>
                    <div>
                      <strong>OrderForm ID:</strong> {cart.orderform_id}
                    </div>
                    <div>
                      <strong>{formatDate(cart.last_activity_at || cart.lastChange)}</strong>
                    </div>
                  </CartItemHeader>
                  
                  <div>
                    <div><strong>Cliente:</strong> {cart.user_email || cart.clientProfileData?.email || 'Não identificado'}</div>
                    <div><strong>CPF/CNPJ:</strong> {cart.user_document || cart.clientProfileData?.document || 'Não informado'}</div>
                    <div><strong>Itens:</strong> {cart.items_count || (cart.items && cart.items.length) || 0}</div>
                    <div><strong>Origem:</strong> {cart.is_app ? 'Aplicativo' : 'Web'}</div>
                    {cart.total_value && (
                      <div><strong>Valor total:</strong> {formatCurrency(cart.total_value)}</div>
                    )}
                  </div>
                  
                  <Spacing size={15} />
                  
                  <ActionButtonsContainer>
                    <Button
                      variant="buttonSecondary"
                      text="Ver Detalhes"
                      onClick={() => fetchCartDetails(cart.id || cart.orderform_id)}
                      style={{ marginRight: '10px' }}
                    />
                    <Button
                      variant="buttonTertiary"
                      text="Copiar Link do Carrinho"
                      onClick={() => copyCartLink(cart)}
                    />
                  </ActionButtonsContainer>
                </CartItemContainer>
              ))}
            </CartItemsList>
          </>
        )}

        {selectedCart && (
          <>
            <ActionButtonsContainer>
              <Button
                variant="buttonSecondary"
                text="Voltar para lista"
                onClick={() => setSelectedCart(null)}
                style={{ marginRight: '10px' }}
              />
              <Button
                variant="buttonTertiary"
                text="Copiar Link do Carrinho"
                onClick={() => copyCartLink(selectedCart)}
              />
            </ActionButtonsContainer>
            
            <Spacing size={20} />
            
            <CartItemContainer>
              <CartItemHeader>
                <div>
                  <Text variant="subtitle">Detalhes do Carrinho</Text>
                </div>
                <div>
                  <strong>Última atividade:</strong> {formatDate(selectedCart.last_activity_at || selectedCart.lastChange)}
                </div>
              </CartItemHeader>
              
              <CartItemDetails>
                <div><strong>OrderForm ID:</strong> {selectedCart.orderform_id || selectedCart.id}</div>
                <div><strong>Cliente:</strong> {selectedCart.user_email || selectedCart.clientProfileData?.email || 'Não identificado'}</div>
                <div><strong>CPF/CNPJ:</strong> {selectedCart.user_document || selectedCart.clientProfileData?.document || 'Não informado'}</div>
                <div><strong>Dispositivo:</strong> {selectedCart.is_app ? 'Aplicativo' : 'Web'}</div>
                <div><strong>Início da sessão:</strong> {formatDate(selectedCart.started_at || selectedCart.creationDate)}</div>
                {selectedCart.completed_at && (
                  <div><strong>Finalizado em:</strong> {formatDate(selectedCart.completed_at)}</div>
                )}
              </CartItemDetails>
              
              <CartDetailsSection>
                <Text variant="subtitle">Itens do Carrinho</Text>
                <Spacing size={10} />
                
                {(selectedCart.items && selectedCart.items.length > 0) ? (
                  <>
                    {selectedCart.items.map((item, index) => (
                      <ItemRow key={index}>
                        <div style={{ flex: 3 }}>
                          <div><strong>{item.product_name || item.name}</strong></div>
                          <div>SKU: {item.sku_id || item.id}</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                          {item.quantity} {item.quantity > 1 ? 'unidades' : 'unidade'}
                        </div>
                        <div style={{ flex: 1, textAlign: 'right' }}>
                          {formatCurrency(item.total_price || (item.price * item.quantity) || 0)}
                        </div>
                      </ItemRow>
                    ))}
                    
                    <CartSummary>
                      {selectedCart.totalizers && selectedCart.totalizers.map((totalizer, index) => (
                        <div key={index}>
                          <span>{totalizer.id === 'Items' ? 'Subtotal' : totalizer.name || totalizer.id}</span>
                          <span>{formatCurrency(totalizer.value)}</span>
                        </div>
                      ))}
                      
                      <div className="total">
                        <span>Total</span>
                        <span>{formatCurrency(selectedCart.value || 0)}</span>
                      </div>
                    </CartSummary>
                  </>
                ) : (
                  <div>Nenhum item no carrinho</div>
                )}
              </CartDetailsSection>
              
              {selectedCart.logisticsInfo && selectedCart.logisticsInfo.length > 0 && (
                <CartDetailsSection>
                  <Text variant="subtitle">Informações de Entrega</Text>
                  <Spacing size={10} />
                  
                  {selectedCart.shippingData && selectedCart.shippingData.address && (
                    <div>
                      <div><strong>Endereço:</strong> {selectedCart.shippingData.address.street}, {selectedCart.shippingData.address.number}</div>
                      <div>{selectedCart.shippingData.address.neighborhood} - {selectedCart.shippingData.address.city}/{selectedCart.shippingData.address.state}</div>
                      <div>CEP: {selectedCart.shippingData.address.postalCode}</div>
                      <Spacing size={10} />
                    </div>
                  )}
                  
                  {selectedCart.logisticsInfo.map((info, index) => (
                    <div key={index}>
                      <div><strong>Método de Entrega:</strong> {info.selectedDeliveryChannel || 'Não selecionado'}</div>
                      {info.selectedSla && (
                        <div><strong>Prazo de Entrega:</strong> {info.selectedSla}</div>
                      )}
                    </div>
                  ))}
                </CartDetailsSection>
              )}
              
              {selectedCart.marketingData && selectedCart.marketingData.coupon && (
                <CartDetailsSection>
                  <Text variant="subtitle">Cupons Aplicados</Text>
                  <Spacing size={10} />
                  <PromotionTag>{selectedCart.marketingData.coupon}</PromotionTag>
                </CartDetailsSection>
              )}
              
              {selectedCart.events && selectedCart.events.length > 0 && (
                <CartDetailsSection>
                  <Text variant="subtitle">Linha do Tempo</Text>
                  <Spacing size={10} />
                  
                  <CartEventTimeline>
                    {selectedCart.events.map((event, index) => (
                      <CartEvent key={index}>
                        <CartEventTime>{formatDate(event.occurred_at)}</CartEventTime>
                        <CartEventType>{getEventDescription(event.event_type)}</CartEventType>
                        
                        {event.data && (
                          <CartEventDetail>
                            {event.event_type === 'item_added' && (
                              <>
                                Produto adicionado: {event.data.sku_id} - Quantidade: {event.data.quantity}
                              </>
                            )}
                            
                            {event.event_type === 'item_removed' && (
                              <>
                                Produto removido: {event.data.sku_id} - Quantidade: {event.data.quantity}
                              </>
                            )}
                            
                            {event.event_type === 'item_updated' && (
                              <>
                                Produto atualizado: {event.data.sku_id} - 
                                Quantidade antiga: {event.data.old_quantity} - 
                                Nova quantidade: {event.data.new_quantity}
                              </>
                            )}
                            
                            {event.event_type === 'promotion_applied' && (
                              <>
                                Promoção aplicada: {event.data.promotions?.[0]?.name || 'Não especificada'}
                              </>
                            )}
                            
                            {event.event_type === 'cart_shared' && event.data.share_url && (
                              <>
                                Link compartilhado: {event.data.share_url}
                              </>
                            )}
                          </CartEventDetail>
                        )}
                      </CartEvent>
                    ))}
                  </CartEventTimeline>
                </CartDetailsSection>
              )}
            </CartItemContainer>
          </>
        )}
      </Container>
    </>
  );
};

export default CartTracking;